import { Stack } from '@mui/material';
import React from 'react';

import { countrySelectSx, drawerButtonSx, drawerFooterSx } from '../../pages/dashboard/DashboardStyles';
import { useTranslation } from '../../utils/translations';
import ButtonComponent from '../button/ButtonComponent';
import { CustomDrawer } from '../drawer/CustomDrawer';
import { CustomSelect } from '../select/CustomSelect';
import { TextFieldComponent } from '../text-field/text-field-component';
import { ACCOUNT_NUMBER_MAX_LENGTH, BANK_NAME_MAX_LENGTH } from '../validations/validations';

interface AddBankAccountFormData {
    accountType: string;
    nickName: string;
    bankName: string;
    routingNumber: string;
    reEnterRoutingNumber: string;
    accountNumber: string;
    reEnterAccountNumber: string;
    accountId: string;
}

interface AddBankAccountDrawerProps {
    open: boolean;
    onClose: () => void;
    formData: AddBankAccountFormData;
    formErrors: Record<string, string>;
    setFormErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    country: string;
    setCountry: (value: string) => void;
    onInputChange: (field: string, value: string) => void;
    onAddAccount: () => void;
    accountTypeOptions: string[];
    showAccountNumber: boolean;
    setShowAccountNumber: (value: boolean) => void;
    showReEnterAccountNumber: boolean;
    setShowReEnterAccountNumber: (value: boolean) => void;
    showRoutingNumber: boolean;
    setShowRoutingNumber: (value: boolean) => void;
    showReEnterRoutingNumber: boolean;
    setShowReEnterRoutingNumber: (value: boolean) => void;
    /** Disables the "Add Account" submit button (e.g. while the form is invalid). */
    addAccountDisabled?: boolean;
    /** Overrides for callers whose account-type select uses different copy. */
    accountTypeAriaLabel?: string;
    accountTypePlaceholder?: string;
    /** Overrides for callers with different copy-prevention behavior on these fields. */
    routingNumberPreventCopy?: boolean;
    accountNumberPreventCopy?: boolean;
}

/** The "Add Account" drawer used on the Make Payment page and the Bank Accounts page. */
export const AddBankAccountDrawer: React.FC<AddBankAccountDrawerProps> = ({
    open,
    onClose,
    formData,
    formErrors,
    setFormErrors,
    country,
    setCountry,
    onInputChange,
    onAddAccount,
    accountTypeOptions,
    showAccountNumber,
    setShowAccountNumber,
    showReEnterAccountNumber,
    setShowReEnterAccountNumber,
    showRoutingNumber,
    setShowRoutingNumber,
    showReEnterRoutingNumber,
    setShowReEnterRoutingNumber,
    addAccountDisabled = false,
    accountTypeAriaLabel,
    accountTypePlaceholder = 'Select Account Type',
    routingNumberPreventCopy = true,
    accountNumberPreventCopy = true,
}) => {
    const { t } = useTranslation();

    return (
        <CustomDrawer
            open={open}
            onClose={onClose}
            title={t('BankAccounts.addNewAccount')}
            width={380}
            footer={
                <Stack direction="row" sx={drawerFooterSx}>
                    <ButtonComponent variant="outlined" onClick={onClose} sx={drawerButtonSx}>
                        {t('common.Cancel')}
                    </ButtonComponent>
                    <ButtonComponent
                        variant="contained"
                        onClick={onAddAccount}
                        disabled={addAccountDisabled}
                        sx={drawerButtonSx}
                    >
                        {t('BankAccounts.addAccount')}
                    </ButtonComponent>
                </Stack>
            }
            sticky={true}
        >
            <Stack gap={2}>
                <CustomSelect
                    title={t('payments.createPayment.country')}
                    ariaLabel={t('payments.createPayment.country')}
                    labelSrOnly
                    required
                    value={country}
                    onChange={value => {
                        setCountry(value);
                        // Clear error when user selects a country
                        if (formErrors.country) {
                            setFormErrors(prev => ({
                                ...prev,
                                country: '',
                            }));
                        }
                    }}
                    options={['United States', 'Canada']}
                    placeholder={t('payments.createPayment.selectYourCountry')}
                    errorValue={formErrors.country}
                    sx={countrySelectSx}
                />
                {/* Account Type */}
                <CustomSelect
                    value={formData.accountType}
                    onChange={value => onInputChange('accountType', value)}
                    options={accountTypeOptions}
                    title={t('BankAccounts.form.accountType')}
                    ariaLabel={accountTypeAriaLabel ?? t('BankAccounts.form.accountType')}
                    labelSrOnly
                    required
                    placeholder={accountTypePlaceholder}
                    errorValue={formErrors.accountType}
                />

                {/* Bank Name */}
                <TextFieldComponent
                    value={formData.bankName}
                    onChange={e => onInputChange('bankName', e.target.value)}
                    errorValue={formErrors.bankName}
                    required
                    maxLength={BANK_NAME_MAX_LENGTH}
                    label={t('BankAccounts.form.bankName')}
                />

                <TextFieldComponent
                    value={formData.nickName}
                    onChange={e => onInputChange('nickName', e.target.value)}
                    label={t('payments.createPayment.nickName')}
                    maxLength={30}
                />
                {/* Routing Number */}
                <TextFieldComponent
                    value={formData.routingNumber}
                    onChange={e => onInputChange('routingNumber', e.target.value)}
                    label={t('BankAccounts.form.routingNumber')}
                    errorValue={formErrors.routingNumber}
                    required
                    maxLength={9}
                    preventCopy={routingNumberPreventCopy}
                    showPasswordToggle={true}
                    onToggleVisibility={setShowRoutingNumber}
                    type={showRoutingNumber ? 'text' : 'password'}
                />
                {/* Re-enter Routing Number */}
                <TextFieldComponent
                    value={formData.reEnterRoutingNumber}
                    onChange={e => onInputChange('reEnterRoutingNumber', e.target.value)}
                    label={t('BankAccounts.form.reEnterRoutingNumber')}
                    errorValue={formErrors.reEnterRoutingNumber}
                    required
                    maxLength={9}
                    preventCopy={true}
                    showPasswordToggle={true}
                    onToggleVisibility={setShowReEnterRoutingNumber}
                    type={showReEnterRoutingNumber ? 'text' : 'password'}
                />
                {/* Account Number */}
                <TextFieldComponent
                    value={formData.accountNumber}
                    onChange={e => onInputChange('accountNumber', e.target.value)}
                    label={t('BankAccounts.form.accountNumber')}
                    errorValue={formErrors.accountNumber}
                    required
                    maxLength={ACCOUNT_NUMBER_MAX_LENGTH}
                    preventCopy={accountNumberPreventCopy}
                    showPasswordToggle={true}
                    onToggleVisibility={setShowAccountNumber}
                    type={showAccountNumber ? 'text' : 'password'}
                />
                {/* Re-enter Account Number */}
                <TextFieldComponent
                    value={formData.reEnterAccountNumber}
                    onChange={e => onInputChange('reEnterAccountNumber', e.target.value)}
                    label={t('BankAccounts.form.reEnterAccountNumber')}
                    errorValue={formErrors.reEnterAccountNumber}
                    required
                    maxLength={ACCOUNT_NUMBER_MAX_LENGTH}
                    preventCopy={true}
                    showPasswordToggle={true}
                    onToggleVisibility={setShowReEnterAccountNumber}
                    type={showReEnterAccountNumber ? 'text' : 'password'}
                />
            </Stack>
        </CustomDrawer>
    );
};
